// src/pages/PaperTrading.js
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../apiClient";
import { useAuth } from "../context/AuthContext";

import BrokerConnectAlpaca from "../components/BrokerConnectAlpaca";
import BrokerConnectTradier from "../components/BrokerConnectTradier";
import BalancesCard from "../components/BalancesCard";
import PlaceOrderCard from "../components/PlaceOrderCard";
import PlaceOptionOrderCard from "../components/PlaceOptionOrderCard1";
import PositionsAndOrdersCard from "../components/PositionsAndOrdersCard";
import TradeSettingsCard from "../components/TradeSettingsCard";
import AllTradesCard from "../components/AllTradesCard";
import AutoTradeRunsCard from "../components/AutoTradeRunsCard";
import InfoTooltip from "../components/InfoTooltip";



import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

function getUserId() {
    return (
        localStorage.getItem("email") ||
        localStorage.getItem("user_id") ||
        localStorage.getItem("trader_id") ||
        ""
    );
}

/* ------------------------------------------------------------------ */
/*  Mode badge                                                         */
/* ------------------------------------------------------------------ */


function TradeModeBadge({ mode }) {
    const label = mode === "live" ? "LIVE" : "PAPER";

    return (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-800 border border-zinc-600 text-xs">
            <span
                className={`h-2 w-2 rounded-full ${mode === "live" ? "bg-red-400" : "bg-emerald-400"
                    } animate-pulse`}
            />
            <span className="font-semibold text-zinc-100">
                Trading Mode: {label}
            </span>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Broker header                                                      */
/* ------------------------------------------------------------------ */

function BrokerHeader() {
    return (
        <div className="p-4 bg-zinc-900 rounded-2xl flex items-center justify-between gap-4">
            <div>
                <h2 className="text-lg font-semibold text-zinc-50">
                    Connected Brokers
                </h2>
                <p className="text-xs text-zinc-400">
                    Link your brokerage APIs or OAuth accounts once, then trade from any page.
                </p>
            </div>
            <div className="flex gap-3">
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-900/60 border border-emerald-500/60 shadow-[0_0_16px_rgba(16,185,129,0.8)]">
                    <span className="h-2 w-2 rounded-full bg-emerald-300 animate-pulse" />
                    <span className="text-xs font-semibold text-emerald-200 tracking-[0.12em]">
                        ALPACA
                    </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/60 border border-indigo-400/60 shadow-[0_0_16px_rgba(129,140,248,0.8)]">
                    <span className="h-2 w-2 rounded-full bg-indigo-300 animate-pulse" />
                    <span className="text-xs font-semibold text-indigo-100 tracking-[0.12em]">
                        TRADIER
                    </span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/60 border border-blue-400/60 shadow-[0_0_16px_rgba(96,165,250,0.65)]">
                    <span className="h-2 w-2 rounded-full bg-blue-300 animate-pulse" />
                    <span className="text-xs font-semibold text-blue-100 tracking-[0.12em]">
                        E*TRADE
                    </span>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  E*TRADE OAuth connect card                                        */
/* ------------------------------------------------------------------ */

function BrokerConnectEtrade({ userId, mode, token }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const etradeMode = mode === "live" ? "live" : "sandbox";

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const loadStatus = async () => {
        if (!userId) {
            setStatus(null);
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const d = await apiFetch(
                `/api/brokers/etrade/status?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(etradeMode)}`,
                { headers }
            );
            setStatus(d || {});
        } catch (e) {
            console.error("E*TRADE status error", e);
            setErr(e?.message || "Could not check E*TRADE status.");
            setStatus(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, etradeMode, token]);

    useEffect(() => {
        const onMessage = (event) => {
            const data = event?.data || {};
            if (data.type !== "ETRADE_OAUTH_COMPLETE") return;
            if (data.ok) {
                setMsg("E*TRADE connected. Refreshing account status…");
                loadStatus();
            } else {
                setErr(data?.payload?.error || "E*TRADE connection failed.");
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, etradeMode]);

    const connect = async () => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        setMsg("");
        try {
            const d = await apiFetch("/api/brokers/etrade/start", {
                method: "POST",
                headers,
                body: JSON.stringify({
                    user_id: userId,
                    mode: etradeMode,
                }),
            });

            if (!d?.auth_url) {
                throw new Error(d?.error || "E*TRADE did not return an authorization URL.");
            }

            const popup = window.open(
                d.auth_url,
                "etrade_oauth",
                "width=900,height=760,noopener=no,noreferrer=no"
            );

            if (!popup) {
                setMsg("Popup blocked. Open this E*TRADE authorization link in a new tab: " + d.auth_url);
            } else {
                setMsg("E*TRADE authorization opened. Approve access, then this card will update automatically.");
            }
        } catch (e) {
            console.error("E*TRADE connect error", e);
            setErr(e?.message || "Could not start E*TRADE connection.");
        } finally {
            setLoading(false);
        }
    };

    const connected = !!status?.connected;
    const accountSaved = !!status?.account_id_key_saved;

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3 border border-zinc-800">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-100">E*TRADE</h3>
                    <p className="text-xs text-zinc-400">
                        OAuth connection for this logged-in user. No E*TRADE username or password is saved in MyStockPicksAI.
                    </p>
                </div>
                <span
                    className={
                        "px-2 py-1 rounded-full border text-[10px] font-semibold " +
                        (connected
                            ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                            : "border-zinc-600 bg-black/30 text-zinc-400")
                    }
                >
                    {connected ? "CONNECTED" : "NOT CONNECTED"}
                </span>
            </div>

            <div className="text-xs text-zinc-400">
                Mode: <span className="font-semibold text-zinc-200">{etradeMode === "live" ? "Live" : "Sandbox"}</span>
                {connected && (
                    <span className="ml-2 text-emerald-300">
                        · Account key {accountSaved ? "saved" : "pending"}
                    </span>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                <button
                    onClick={connect}
                    disabled={loading || !userId}
                    className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs font-semibold disabled:opacity-60"
                >
                    {connected ? "Reconnect E*TRADE" : "Connect E*TRADE"}
                </button>
                <button
                    onClick={loadStatus}
                    disabled={loading || !userId}
                    className="px-3 py-2 rounded-lg bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 text-xs disabled:opacity-60"
                >
                    {loading ? "Checking…" : "Check Status"}
                </button>
            </div>

            {msg && <div className="text-[11px] text-blue-200 break-words">{msg}</div>}
            {err && <div className="text-[11px] text-red-400 break-words">{err}</div>}

            <div className="text-[10px] text-zinc-500">
                Bot note: after this card shows connected, turn on E*TRADE Equities in AI Bot → Broker Modules.
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  AI: Place from Picks                                              */
/* ------------------------------------------------------------------ */

function PicksButtons({ userId, tradeMode, token }) {
    const [msg, setMsg] = useState("");

    const fire = async (url, body) => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        const mode =
            (tradeMode || localStorage.getItem("trade_mode") || "paper").toLowerCase();

        setMsg("Working…");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const data = await apiFetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    ...body,
                    mode,
                }),
            });

            const text =
                data?.message ||
                data?.status ||
                (data?.ok ? "OK" : JSON.stringify(data || {}));
            setMsg(text);
        } catch (err) {
            console.error("PicksButtons error", err);
            const text = err?.message || "Request failed.";
            setMsg(text);
        }
    };

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <h3 className="text-lg font-semibold text-zinc-100">
                Quick AI trades from Daily Picks
            </h3>
            <p className="text-xs text-zinc-400">
                Uses your current mode (&quot;paper&quot; or &quot;live&quot;) plus
                your per-trade size and risk settings.
            </p>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() =>
                        fire("/api/ai/trade", {
                            user_id: userId,
                            strategy: "daily_picks",
                            max_trades: 1,
                        })
                    }
                    className="bg-blue-600 hover:bg-blue-500 rounded px-4 py-2 text-xs sm:text-sm font-semibold"
                >
                    Place Equity from Picks
                </button>
                <button
                    onClick={() =>
                        fire("/api/ai/options/trade", {
                            user_id: userId,
                            strategy: "daily_picks_options",
                            max_trades: 1,
                        })
                    }
                    className="bg-purple-600 hover:bg-purple-500 rounded px-4 py-2 text-xs sm:text-sm font-semibold"
                >
                    Place Options from Picks
                </button>
            </div>

            {msg && (
                <div className="text-xs text-zinc-200 whitespace-pre-wrap break-words">
                    {msg}
                </div>
            )}
        </div>
    );
}


function AutoStatusBanner({ userId, token, tradeMode }) {
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) {
            setSnapshot(null);
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const mode =
                (tradeMode || localStorage.getItem("trade_mode") || "paper")
                    .toLowerCase() === "live"
                    ? "live"
                    : "paper";
            const d = await apiFetch(
                `/api/ai/auto-trade-readiness?user_id=${encodeURIComponent(
                    userId
                )}&mode=${mode}`,
                { headers }
            );
            setSnapshot(d || {});
        } catch (e) {
            console.error("AutoStatusBanner readiness error:", e);
            setErr("Could not load auto-trade status.");
            setSnapshot(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token]);

    if (!userId) {
        return (
            <div className="mt-3 p-3 rounded-2xl border border-zinc-700 bg-zinc-900 text-xs text-zinc-400">
                Sign in to enable Auto Trade and Smart Exits.
            </div>
        );
    }

    const checks = snapshot?.checks || [];
    const settings = snapshot?.trade_settings || {};

    const findCheck = (key) => checks.find((c) => c.key === key) || {};

    const autoTradeCheck = findCheck("auto_trade_enabled");
    const autoTradeOn = !!(autoTradeCheck.value || autoTradeCheck.ok);

    const alpacaCheck = findCheck("alpaca_connection");
    const tradierCheck = findCheck("tradier_connection");
    const windowCheck = findCheck("trading_window_now");
    const settingsCheck = findCheck("trade_settings");

    const anyBrokerOk = !!(alpacaCheck.ok || tradierCheck.ok);
    const autoExitOn = !!(settings.auto_exit_enabled);
    const tradingWindowOpen = !!(windowCheck.ok);

    const showBrokerWarning = autoTradeOn && !anyBrokerOk;

    const bgClass = autoTradeOn
        ? "bg-gradient-to-r from-emerald-500/10 via-sky-500/10 to-purple-500/10 border-emerald-500/40"
        : "bg-zinc-900 border-zinc-700";

    const mainText = autoTradeOn
        ? "Auto Trade is ACTIVE"
        : "Auto Trade is OFF – enable it in Trade Settings when you're ready.";

    return (
        <div className={`mt-3 p-3 rounded-2xl border ${bgClass}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] text-zinc-400">
                        Automation status
                    </div>
                    <div className="text-sm sm:text-base font-semibold text-zinc-100 truncate">
                        {mainText}
                    </div>
                    {settingsCheck?.missing &&
                        settingsCheck.missing.length > 0 && (
                            <div className="mt-1 text-[11px] text-amber-300">
                                Missing trade settings:{" "}
                                {settingsCheck.missing.join(", ")}
                            </div>
                        )}
                </div>

                <div className="flex flex-wrap gap-2 text-[10px] sm:text-xs">
                    <span
                        className={
                            "px-2 py-1 rounded-full border " +
                            (autoTradeOn
                                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                                : "border-zinc-600 bg-zinc-900 text-zinc-300")
                        }
                    >
                        Auto Trade:{" "}
                        <strong>{autoTradeOn ? "ON" : "OFF"}</strong>
                    </span>
                    <span
                        className={
                            "px-2 py-1 rounded-full border " +
                            (autoExitOn
                                ? "border-sky-500/60 bg-sky-500/10 text-sky-300"
                                : "border-zinc-600 bg-zinc-900 text-zinc-300")
                        }
                    >
                        Auto Exits:{" "}
                        <strong>{autoExitOn ? "ON" : "OFF"}</strong>
                    </span>
                    <span
                        className={
                            "px-2 py-1 rounded-full border " +
                            (tradingWindowOpen
                                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                                : "border-zinc-600 bg-zinc-900 text-zinc-300")
                        }
                    >
                        Window:{" "}
                        <strong>
                            {tradingWindowOpen ? "OPEN" : "CLOSED"}
                        </strong>
                    </span>
                </div>
            </div>

            {showBrokerWarning && (
                <div className="mt-2 text-[11px] text-amber-300">
                    Auto Trade is ON but no broker connection is active. Connect
                    Alpaca, E*TRADE, or Tradier on this page before relying on cron
                    auto-buys.
                </div>
            )}

            {err && (
                <div className="mt-2 text-[11px] text-red-400">{err}</div>
            )}

            <div className="mt-2 flex items-center justify-between text-[10px] text-zinc-500">
                <div>
                    Brokers: Alpaca{" "}
                    {alpacaCheck.ok ? "✅" : "❌"} · Tradier{" "}
                    {tradierCheck.ok ? "✅" : "❌"} · E*TRADE check is shown in the E*TRADE card
                </div>
                <button
                    onClick={load}
                    className="px-2 py-0.5 rounded-full border border-zinc-700 bg-black/30 hover:bg-zinc-800 text-[10px] text-zinc-300"
                    disabled={loading}
                >
                    {loading ? "Checking…" : "Refresh"}
                </button>
            </div>
        </div>
    );
}



/* ------------------------------------------------------------------ */
/*  Auto-trade readiness + summary                                   */
/* ------------------------------------------------------------------ */

function AutoTradeReadiness({ userId, token, tradeMode }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const mode =
                (tradeMode || localStorage.getItem("trade_mode") || "paper")
                    .toLowerCase() === "live"
                    ? "live"
                    : "paper";

            const d = await apiFetch(
                `/api/ai/auto-trade-readiness?user_id=${encodeURIComponent(
                    userId
                )}&mode=${mode}`,
                { headers }
            );

            setData(d || {});
        } catch (e) {
            console.error("AutoTradeReadiness error:", e);
            setErr("Failed to load readiness.");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token]);

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                    Auto-Trade Readiness
                    <InfoTooltip
                        text="Checks trade settings, broker connections, and trading window to see if auto-trades are allowed to fire."
                    />
                </h3>

                <button
                    onClick={load}
                    className="bg-zinc-700 hover:bg-zinc-600 rounded px-3 py-1 text-xs"
                >
                    {loading ? "Checking…" : "Check"}
                </button>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            {data && !err && (
                <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-40 overflow-y-auto border border-zinc-800 rounded p-2 bg-black/30">
                    {JSON.stringify(data, null, 2)}
                </pre>
            )}
        </div>
    );
}

function AutoTradeSummary({ userId, token }) {
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const d = await apiFetch(
                `/api/ai/auto-trade-summary?user_id=${encodeURIComponent(userId)}`,
                { headers }
            );

            if (!d || d.ok === false) {
                setErr(d?.error || "Could not load auto-trade history.");
                setSummary(null);
                setRecent([]);
            } else {
                setSummary({
                    total_runs: d.total_runs ?? 0,
                    successes: d.successes ?? 0,
                    errors: d.errors ?? 0,
                    last_run_at: d.last_run_at ?? d.last_run ?? null,
                });
                setRecent(d.recent || []);
            }
        } catch (e) {
            console.error("AutoTradeSummary error:", e);
            setErr("Could not load auto-trade history.");
            setSummary(null);
            setRecent([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token]);

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Auto-trade runs</h3>
                <button
                    onClick={load}
                    className="bg-zinc-700 hover:bg-zinc-600 rounded px-3 py-1 text-xs"
                >
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            {summary && (
                <>
                    <div className="flex flex-wrap gap-4 text-xs">
                        <div>
                            <div className="text-zinc-400">Total</div>
                            <div className="text-zinc-50 font-semibold">
                                {summary.total_runs}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Last run</div>
                            <div className="text-zinc-50 font-semibold">
                                {summary.last_run_at || "—"}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Successes</div>
                            <div className="text-emerald-400 font-semibold">
                                {summary.successes}
                            </div>
                        </div>
                        <div>
                            <div className="text-zinc-400">Errors</div>
                            <div className="text-red-400 font-semibold">
                                {summary.errors}
                            </div>
                        </div>
                    </div>

                    <div className="text-xs text-zinc-400 mt-2 mb-1">
                        Last 10 auto-trades
                    </div>
                    <div className="max-h-40 overflow-y-auto text-[11px] space-y-1">
                        {recent.length === 0 && (
                            <div className="text-zinc-500">No auto-trades logged yet.</div>
                        )}
                        {recent.map((row) => {
                            const statusClass =
                                (row.status || "").toLowerCase() === "success"
                                    ? "text-emerald-400"
                                    : "text-amber-400";

                            return (
                                <div
                                    key={row.id}
                                    className="flex justify-between py-1 border-b border-zinc-800 last:border-0"
                                >
                                    <div className="mr-2 min-w-0">
                                        <div className="font-medium text-zinc-100 truncate">
                                            {row.symbol || row.option_symbol || "—"}
                                        </div>
                                        <div className="text-zinc-400">
                                            {row.asset_type} · {row.side} · {row.broker}
                                        </div>

                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {row.strategy && (
                                                <span className="px-1.5 py-px rounded-full bg-zinc-800 text-[10px] text-zinc-200 truncate">
                                                    {row.strategy}
                                                </span>
                                            )}
                                            {row.source && row.source !== row.strategy && (
                                                <span className="px-1.5 py-px rounded-full bg-blue-900/40 text-[10px] text-blue-200 truncate">
                                                    {row.source}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right text-[11px]">
                                        <div className={statusClass}>
                                            {row.status || "pending"}
                                        </div>
                                        <div className="text-zinc-500 truncate">
                                            {row.created_at}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                </>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Auto Exit Toggle                                                  */
/* ------------------------------------------------------------------ */

function AutoExitToggle({ userId, token }) {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const d = await apiFetch(
                `/api/trade/settings?user_id=${encodeURIComponent(userId)}`,
                { headers }
            );
            const flag = !!(d?.auto_exit_enabled);
            setEnabled(flag);
        } catch (e) {
            console.error("AutoExitToggle load error", e);
            setErr("Could not load auto-exit status.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token]);

    const toggle = async () => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const next = !enabled;
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await apiFetch("/api/trade/settings", {
                method: "POST",
                headers,
                body: JSON.stringify({
                    user_id: userId,
                    auto_exit_enabled: next ? 1 : 0,
                }),
            });
            setEnabled(next);
        } catch (e) {
            console.error("AutoExitToggle save error", e);
            setErr(e?.message || "Failed to update auto-exit setting.");
        } finally {
            setLoading(false);
        }
    };

    const statusLabel = enabled ? "ACTIVE" : "INACTIVE";
    const statusColor = enabled ? "text-emerald-400" : "text-red-400";
    const dotColor = enabled ? "bg-emerald-400" : "bg-red-400";

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-100">Auto Exits</h3>
                    <p className="text-xs text-zinc-400">
                        Turn the Smart Exit engine on/off for your open positions when the
                        cron runs.
                    </p>
                </div>
                <button
                    onClick={toggle}
                    disabled={loading || !userId}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs disabled:opacity-60"
                >
                    <span
                        className={`h-2.5 w-2.5 rounded-full ${dotColor} animate-pulse`}
                    />
                    <span className="font-semibold text-zinc-100">
                        {enabled ? "ON" : "OFF"}
                    </span>
                </button>
            </div>

            <div className="text-xs text-zinc-400">
                Status:{" "}
                <span className={`font-semibold ${statusColor}`}>{statusLabel}</span>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            <div className="text-[10px] text-zinc-500">
                Note: Auto Exits only run if you have open positions and your cron calls{" "}
                <code>/api/tasks/auto-manage-exits</code>.
            </div>
        </div>
    );
}


/* ------------------------------------------------------------------ */
/*  Auto Trade Toggle                                                 */
/* ------------------------------------------------------------------ */

function AutoTradeToggle({ userId, token }) {
    const [enabled, setEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const d = await apiFetch(
                `/api/trade/settings?user_id=${encodeURIComponent(userId)}`,
                { headers }
            );
            const flag = !!(d?.auto_trade_enabled);
            setEnabled(flag);
        } catch (e) {
            console.error("AutoTradeToggle load error", e);
            setErr("Could not load auto-trade status.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token]);

    const toggle = async () => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        try {
            const next = !enabled;
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            await apiFetch("/api/trade/settings", {
                method: "POST",
                headers,
                body: JSON.stringify({
                    user_id: userId,
                    auto_trade_enabled: next ? 1 : 0,
                }),
            });
            setEnabled(next);
        } catch (e) {
            console.error("AutoTradeToggle save error", e);
            setErr(e?.message || "Failed to update auto-trade setting.");
        } finally {
            setLoading(false);
        }
    };

    const statusLabel = enabled ? "ACTIVE" : "INACTIVE";
    const statusColor = enabled ? "text-emerald-400" : "text-red-400";
    const dotColor = enabled ? "bg-emerald-400" : "bg-red-400";

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-zinc-100">Auto Trade</h3>
                    <p className="text-xs text-zinc-400">
                        Turn the AI auto-buy engine on/off. Cron jobs will only place new
                        trades when this is ON.
                    </p>
                </div>
                <button
                    onClick={toggle}
                    disabled={loading || !userId}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-xs disabled:opacity-60"
                >
                    <span
                        className={`h-2.5 w-2.5 rounded-full ${dotColor} animate-pulse`}
                    />
                    <span className="font-semibold text-zinc-100">
                        {enabled ? "ON" : "OFF"}
                    </span>
                </button>
            </div>

            <div className="text-xs text-zinc-400">
                Status:{" "}
                <span className={`font-semibold ${statusColor}`}>{statusLabel}</span>
            </div>

            {err && <div className="text-xs text-red-400">{err}</div>}

            <div className="text-[10px] text-zinc-500">
                Note: Auto Trade also requires valid broker connections and passes all
                checks in <code>Auto-Trade Readiness</code>.
            </div>
        </div>
    );
}



/* ------------------------------------------------------------------ */
/*  Trade analytics helpers                                           */
/* ------------------------------------------------------------------ */

function formatCurrency(n) {
    if (n === null || n === undefined) return "—";
    const value = Number(n) || 0;
    const sign = value < 0 ? "-" : "";
    return `${sign}$${Math.abs(value).toFixed(2)}`;
}

// PnlOverTimeChart, TickerHeatmap, getTradeTicker, EquityOptionsPie,
// TickerDrilldownModal stay exactly as in your file (no network calls),
// so I’m not repeating them here for brevity — just keep your existing
// implementations.

//
//  ... keep your existing implementations of:
//  PnlOverTimeChart
//  TickerHeatmap
//  getTradeTicker
//  EquityOptionsPie
//  TickerDrilldownModal
//

/* ------------------------------------------------------------------ */
/*  TradeAnalyticsCard – add token + apiFetch                         */
/* ------------------------------------------------------------------ */

function TradeAnalyticsCard({ userId, token, mode = "paper" }) {
    const [stats, setStats] = useState(null);
    const [pnlSeries, setPnlSeries] = useState([]);
    const [tickerPnl, setTickerPnl] = useState([]);
    const [tickerMeta, setTickerMeta] = useState({});
    const [rawTrades, setRawTrades] = useState([]);
    const [viewMode, setViewMode] = useState("daily"); // "daily" | "cum"
    const [selectedTicker, setSelectedTicker] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const load = async () => {
        if (!userId) return;
        setLoading(true);
        setErr("");

        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const d = await apiFetch(
                `/api/trades/history?user_id=${encodeURIComponent(
                    userId
                )}&mode=${encodeURIComponent(mode || "paper")}&page=1&limit=500`,
                { headers }
            );

            const trades = d.trades || d.items || d || [];
            if (!Array.isArray(trades) || trades.length === 0) {
                setStats(null);
                setPnlSeries([]);
                setTickerPnl([]);
                setTickerMeta({});
                setRawTrades([]);
                return;
            }

            setRawTrades(trades);

            const toNumberOrNull = (value) => {
                if (value === null || value === undefined || value === "") return null;
                const n = Number(value);
                return Number.isFinite(n) ? n : null;
            };

            const getRealizedPnL = (t) => {
                const direct =
                    toNumberOrNull(t.realized_pl) ??
                    toNumberOrNull(t.realized_pnl) ??
                    toNumberOrNull(t.pnl) ??
                    toNumberOrNull(t.pl) ??
                    toNumberOrNull(t.profit_loss);

                if (direct !== null) return direct;

                // Some rows may stash analytics details in extra JSON.
                try {
                    const extra = typeof t.extra === "string" ? JSON.parse(t.extra) : t.extra;
                    return (
                        toNumberOrNull(extra?.realized_pl) ??
                        toNumberOrNull(extra?.pnl_usd) ??
                        toNumberOrNull(extra?.profit_usd) ??
                        null
                    );
                } catch (_) {
                    return null;
                }
            };

            const getTicker = (t) => {
                const raw = (
                    t.symbol ||
                    t.ticker ||
                    t.underlying_symbol ||
                    ""
                ).toString().toUpperCase().trim();
                if (raw) return raw;

                // OCC option symbols start with the underlying root before the date.
                const opt = (t.option_symbol || "").toString().toUpperCase().trim();
                const m = opt.match(/^[A-Z]+/);
                return m ? m[0] : "UNKNOWN";
            };

            const isCompletedRealizedTrade = (t) => {
                const pnl = getRealizedPnL(t);
                if (pnl === null) return false;

                const status = (t.status || "").toString().toLowerCase();
                if (["error", "failed", "rejected", "cancelled", "canceled", "simulated"].includes(status)) {
                    return false;
                }

                // Buy/open rows are entries, not completed P/L. Exit rows are the ones
                // Smart Exit / PUT Bot should log with realized_pl.
                const side = (t.side || "").toString().toLowerCase();
                const source = (t.source || "").toString().toLowerCase();
                const strategy = (t.strategy || t.strategy_tag || "").toString().toLowerCase();
                const looksLikeExit =
                    side.includes("sell") ||
                    side.includes("close") ||
                    source.includes("exit") ||
                    strategy.includes("exit");

                return looksLikeExit || status === "closed" || status === "filled" || status === "success";
            };

            const completedTrades = trades.filter(isCompletedRealizedTrade);

            if (completedTrades.length === 0) {
                setStats(null);
                setPnlSeries([]);
                setTickerPnl([]);
                setTickerMeta({});
                return;
            }

            let total = completedTrades.length;
            let wins = 0;
            let losses = 0;
            let breakeven = 0;
            let realized = 0;
            let equityCount = 0;
            let optionCount = 0;

            const daily = {};
            const perTicker = {};

            completedTrades.forEach((t) => {
                const pnl = getRealizedPnL(t) ?? 0;
                if (pnl > 0) wins += 1;
                else if (pnl < 0) losses += 1;
                else breakeven += 1;

                realized += pnl;

                const asset = (t.asset_type || "").toString().toLowerCase();
                const isOption = asset.includes("option") || !!t.option_symbol;

                if (isOption) optionCount += 1;
                else equityCount += 1;

                const ts = t.closed_at || t.filled_at || t.created_at || t.timestamp;
                const dKey = ts ? String(ts).slice(0, 10) : "unknown";

                if (!daily[dKey]) {
                    daily[dKey] = { total: 0, equity: 0, option: 0 };
                }
                daily[dKey].total += pnl;
                if (isOption) daily[dKey].option += pnl;
                else daily[dKey].equity += pnl;

                const ticker = getTicker(t);
                if (!perTicker[ticker]) {
                    perTicker[ticker] = {
                        pnl: 0,
                        trades: 0,
                        wins: 0,
                        losses: 0,
                        breakeven: 0,
                    };
                }
                perTicker[ticker].pnl += pnl;
                perTicker[ticker].trades += 1;
                if (pnl > 0) perTicker[ticker].wins += 1;
                else if (pnl < 0) perTicker[ticker].losses += 1;
                else perTicker[ticker].breakeven += 1;
            });

            const winRate = total ? (wins / total) * 100 : 0;
            const avgPnL = total ? realized / total : 0;

            const sortedDates = Object.keys(daily).sort((a, b) => a.localeCompare(b));
            let runningTotal = 0;
            let runningEquity = 0;
            let runningOption = 0;

            const pnlSeriesArr = sortedDates.map((date) => {
                const day = daily[date];
                runningTotal += day.total;
                runningEquity += day.equity;
                runningOption += day.option;

                return {
                    date,
                    pnlTotal: day.total,
                    pnlEquity: day.equity,
                    pnlOption: day.option,
                    cumTotal: runningTotal,
                    cumEquity: runningEquity,
                    cumOption: runningOption,
                };
            });

            const tickerArr = Object.entries(perTicker).map(([ticker, v]) => ({
                ticker,
                pnl: v.pnl,
                trades: v.trades,
                wins: v.wins,
                losses: v.losses,
                breakeven: v.breakeven,
            }));

            setStats({
                total,
                wins,
                losses,
                breakeven,
                winRate,
                realized,
                avgPnL,
                equityCount,
                optionCount,
                rawCount: trades.length,
            });
            setPnlSeries(pnlSeriesArr);
            setTickerPnl(tickerArr);
            setTickerMeta(perTicker);
        } catch (e) {
            console.error("TradeAnalyticsCard error:", e);
            setErr("Could not load trade analytics.");
            setStats(null);
            setPnlSeries([]);
            setTickerPnl([]);
            setTickerMeta({});
            setRawTrades([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [userId, token, mode]);

    const hasData = stats && pnlSeries.length > 0;

    const chartKeyTotal =
        viewMode === "daily" ? "pnlTotal" : "cumTotal";
    const chartKeyEquity =
        viewMode === "daily" ? "pnlEquity" : "cumEquity";
    const chartKeyOption =
        viewMode === "daily" ? "pnlOption" : "cumOption";

    const topTickers = [...tickerPnl]
        .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
        .slice(0, 8);

    const pieData = stats
        ? [
            { name: "Equity trades", value: stats.equityCount || 0 },
            { name: "Options trades", value: stats.optionCount || 0 },
        ]
        : [];

    const formatPnL = (v) => formatCurrency(v);

    if (!userId) {
        return (
            <div className="p-4 bg-zinc-900 rounded-2xl text-xs text-zinc-400">
                Sign in to see trade analytics.
            </div>
        );
    }

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <h3 className="text-lg font-semibold">
                        Trade Analytics
                    </h3>
                    <p className="text-xs text-zinc-400">
                        Based on completed trades with realized P&amp;L for the selected Paper/Live mode.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="px-3 py-1 text-xs rounded-full border border-zinc-700 bg-black/30 hover:bg-zinc-800 text-zinc-200 disabled:opacity-60"
                >
                    {loading ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {err && (
                <div className="text-xs text-red-400">{err}</div>
            )}

            {!loading && !err && !hasData && (
                <div className="text-xs text-zinc-400">
                    No completed trades with realized P&amp;L yet for this mode.
                    Entries/buys are not counted as wins; analytics starts showing
                    once Smart Exit, manual sells, or PUT Bot exits log realized P&amp;L.
                </div>
            )}

            {hasData && (
                <>
                    {/* Summary stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="p-2 rounded-lg bg-zinc-800">
                            <div className="text-zinc-400">
                                Total trades
                            </div>
                            <div className="text-lg font-semibold text-zinc-100">
                                {stats.total}
                            </div>
                        </div>
                        <div className="p-2 rounded-lg bg-zinc-800">
                            <div className="text-zinc-400">
                                Win rate
                            </div>
                            <div className="text-lg font-semibold text-emerald-400">
                                {stats.winRate.toFixed(1)}%
                            </div>
                        </div>
                        <div className="p-2 rounded-lg bg-zinc-800">
                            <div className="text-zinc-400">
                                Realized P&amp;L
                            </div>
                            <div
                                className={
                                    "text-lg font-semibold " +
                                    (stats.realized >= 0
                                        ? "text-emerald-400"
                                        : "text-red-400")
                                }
                            >
                                {formatPnL(stats.realized)}
                            </div>
                        </div>
                        <div className="p-2 rounded-lg bg-zinc-800">
                            <div className="text-zinc-400">
                                Avg P&amp;L / trade
                            </div>
                            <div
                                className={
                                    "text-lg font-semibold " +
                                    (stats.avgPnL >= 0
                                        ? "text-emerald-400"
                                        : "text-red-400")
                                }
                            >
                                {formatPnL(stats.avgPnL)}
                            </div>
                        </div>
                    </div>

                    {/* P&L over time */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-zinc-400">
                                P&amp;L over time
                            </span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() =>
                                        setViewMode("daily")
                                    }
                                    className={
                                        "px-2 py-0.5 rounded-full border text-[11px] " +
                                        (viewMode === "daily"
                                            ? "bg-zinc-100 text-black border-zinc-100"
                                            : "bg-zinc-900 text-zinc-300 border-zinc-700")
                                    }
                                >
                                    Daily
                                </button>
                                <button
                                    onClick={() =>
                                        setViewMode("cum")
                                    }
                                    className={
                                        "px-2 py-0.5 rounded-full border text-[11px] " +
                                        (viewMode === "cum"
                                            ? "bg-zinc-100 text-black border-zinc-100"
                                            : "bg-zinc-900 text-zinc-300 border-zinc-700")
                                    }
                                >
                                    Cumulative
                                </button>
                            </div>
                        </div>

                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={pnlSeries}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#27272a"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10 }}
                                        stroke="#a1a1aa"
                                    />
                                    <YAxis
                                        tick={{ fontSize: 10 }}
                                        stroke="#a1a1aa"
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            formatPnL(value),
                                            name,
                                        ]}
                                    />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey={chartKeyTotal}
                                        name={
                                            viewMode === "daily"
                                                ? "Daily total"
                                                : "Cum total"
                                        }
                                        stroke="#22c55e"
                                        dot={false}
                                        strokeWidth={1.8}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey={chartKeyEquity}
                                        name={
                                            viewMode === "daily"
                                                ? "Daily equity"
                                                : "Cum equity"
                                        }
                                        stroke="#60a5fa"
                                        dot={false}
                                        strokeWidth={1.2}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey={chartKeyOption}
                                        name={
                                            viewMode === "daily"
                                                ? "Daily options"
                                                : "Cum options"
                                        }
                                        stroke="#f97316"
                                        dot={false}
                                        strokeWidth={1.2}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Per-ticker + allocation */}
                    <div className="grid md:grid-cols-2 gap-4 text-xs">
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-zinc-400">
                                    Top tickers by P&amp;L
                                </span>
                            </div>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                {topTickers.map((t) => {
                                    const sel =
                                        selectedTicker === t.ticker;
                                    return (
                                        <button
                                            key={t.ticker}
                                            onClick={() =>
                                                setSelectedTicker(
                                                    sel
                                                        ? null
                                                        : t.ticker
                                                )
                                            }
                                            className={
                                                "w-full flex items-center justify-between px-2 py-1 rounded " +
                                                (sel
                                                    ? "bg-zinc-800"
                                                    : "bg-zinc-900 hover:bg-zinc-800")
                                            }
                                        >
                                            <span className="font-semibold text-zinc-100">
                                                {t.ticker}
                                            </span>
                                            <span
                                                className={
                                                    t.pnl >= 0
                                                        ? "text-emerald-400"
                                                        : "text-red-400"
                                                }
                                            >
                                                {formatPnL(t.pnl)}
                                            </span>
                                            <span className="text-[10px] text-zinc-400">
                                                {t.wins}/{t.trades}{" "}
                                                wins
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedTicker && (
                                <div className="text-[11px] text-zinc-400">
                                    Selected:{" "}
                                    <span className="font-semibold">
                                        {selectedTicker}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            <span className="text-zinc-400">
                                Equity vs options trades
                            </span>
                            <div className="h-40">
                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={60}
                                            label
                                        >
                                            {pieData.map(
                                                (entry, index) => (
                                                    <Cell
                                                        key={index}
                                                        fill={
                                                            index === 0
                                                                ? "#22c55e"
                                                                : "#3b82f6"
                                                        }
                                                    />
                                                )
                                            )}
                                        </Pie>
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Unified positions                                                 */
/* ------------------------------------------------------------------ */


function UnifiedPositionsBlock({ userId, mode, onRefresh, positionsRefreshKey }) {
    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                    Positions &amp; Orders
                    <InfoTooltip
                        text="Live view of your open positions and recent orders for the selected mode (Paper or Live)."
                    />
                </h3>

                <button
                    type="button"
                    onClick={onRefresh}
                    className="text-xs px-3 py-1 rounded-full border border-zinc-600 hover:border-zinc-400 text-zinc-200"
                >
                    Refresh
                </button>
            </div>

            {/* key forces a remount → PositionsAndOrdersCard re-runs its loaders */}
            <PositionsAndOrdersCard
                key={positionsRefreshKey}
                userId={userId}
                mode={mode}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Smart Exit Debug UI                                               */
/* ------------------------------------------------------------------ */

function SmartExitDebugCard({ userId, tradeMode, debugMode, token }) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    const hasTradierNoPositions = result
        ? (
            (Array.isArray(result.errors) &&
                result.errors.some(
                    (e) =>
                        typeof e === "string" &&
                        e.includes('positions":"null')
                )) ||
            (Array.isArray(result.results) &&
                result.results.some(
                    (r) =>
                        Array.isArray(r.errors) &&
                        r.errors.some(
                            (e) =>
                                typeof e === "string" &&
                                e.includes('positions":"null')
                        )
                ))
        )
        : false;

    const hasAnyExits = result
        ? Array.isArray(result.results) &&
        result.results.some(
            (r) => Array.isArray(r.exits) && r.exits.length > 0
        )
        : false;


    const run = async (execute) => {
        if (!userId) {
            alert("Please log in first.");
            return;
        }
        setLoading(true);
        setErr("");
        setResult(null);
        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const d = await apiFetch("/api/tasks/auto-manage-exits", {
                method: "POST",
                headers,
                body: JSON.stringify({
                    user_id: userId,
                    mode: tradeMode,
                    debug: debugMode || true,
                    execute: !!execute,
                }),
            });
            setResult(d || {});
        } catch (e) {
            console.error("SmartExitDebug error", e);
            setErr(e?.message || "Exit engine call failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 bg-zinc-900 rounded-2xl space-y-3">
            <h3 className="text-lg font-semibold">
                Smart Exit – Debug
                <InfoTooltip
                    text="Simulates or runs your auto-exit engine so you can see which positions would be closed and why."
                />
            </h3>
            <p className="text-xs text-zinc-400">
                Simulate or run your Smart Exit engine for the current mode (
                <span className="font-semibold">{tradeMode}</span>). Debug mode
                returns detailed reasoning.
            </p>
            <div className="flex flex-wrap gap-2">
                <button
                    onClick={() => run(false)}
                    className="px-3 py-1.5 text-xs rounded-full bg-zinc-700 hover:bg-zinc-600"
                    disabled={loading}
                >
                    {loading ? "Running…" : "Simulate exits (no trades)"}
                </button>
                <button
                    onClick={() => run(true)}
                    className="px-3 py-1.5 text-xs rounded-full bg-rose-600 hover:bg-rose-500"
                    disabled={loading}
                >
                    {loading ? "Running…" : "Run exits now"}
                </button>
            </div>
            {err && <div className="text-xs text-red-400">{err}</div>}
            {result && (
                <>
                    {!hasAnyExits && hasTradierNoPositions && (
                        <div className="text-xs text-zinc-400 mb-1">
                            No Tradier positions found for this mode (
                            <span className="font-semibold">
                                {tradeMode}
                            </span>
                            ). Nothing to exit.
                        </div>
                    )}
                    {!hasAnyExits && !hasTradierNoPositions && (
                        <div className="text-xs text-zinc-400 mb-1">
                            No exits were generated for any broker for this
                            run.
                        </div>
                    )}
                    <pre className="text-[11px] text-zinc-300 whitespace-pre-wrap max-h-56 overflow-y-auto border border-zinc-800 rounded p-2 bg-black/40">
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Tabs + Floating buttons                                           */
/* ------------------------------------------------------------------ */

function TabButton({ id, label, icon, active, onClick }) {
    return (
        <button
            onClick={() => onClick(id)}
            className={`flex items-center gap-1 px-3 py-2 text-xs sm:text-sm border-b-2 -mb-px whitespace-nowrap ${active
                    ? "border-blue-500 text-blue-300"
                    : "border-transparent text-zinc-400 hover:text-zinc-200 hover:border-zinc-600"
                }`}
        >
            {icon && <span className="text-xs">{icon}</span>}
            <span>{label}</span>
        </button>
    );
}

function FloatingTradeButtons({ onGoToOrders, onGoToPositions }) {
    return (
        <div className="fixed bottom-4 right-4 z-40 flex flex-col gap-2 items-end">
            <button
                onClick={onGoToOrders}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-emerald-400 to-emerald-600 text-black shadow-[0_0_22px_rgba(16,185,129,0.9)] animate-pulse"
            >
                Trade
            </button>
            <button
                onClick={onGoToPositions}
                className="px-3 py-1 rounded-full text-xs bg-zinc-900/90 text-zinc-100 border border-zinc-600 hover:bg-zinc-800"
            >
                Positions
            </button>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  MAIN PAGE                                                         */
/* ------------------------------------------------------------------ */

export default function PaperTrading() {
    // ---- AUTH + USER ID FIX ----
    const { user, token } = useAuth();
    const [userId, setUserId] = useState("");

    // fallback for older localStorage keys
    const getUserIdFallback = () => {
        return (
            localStorage.getItem("email") ||
            localStorage.getItem("trader_id") ||
            ""
        );
    };

    useEffect(() => {
        const id = user?.email || getUserIdFallback();
        setUserId(id);
    }, [user]);
    // ---- END FIX ----


    const [activeTab, setActiveTab] = useState("overview");
    const [tradeMode, setTradeMode] = useState(() => {
        const saved = (localStorage.getItem("trade_mode") || "paper").toLowerCase();
        return saved === "live" ? "live" : "paper";
    });
    const [debugMode, setDebugMode] = useState(false);

    // Positions & Orders manual refresh key
    const [positionsRefreshKey, setPositionsRefreshKey] = useState(0);

    const positionsRef = useRef(null);


    useEffect(() => {
        localStorage.setItem("trade_mode", tradeMode);
    }, [tradeMode]);

    // Whenever trade mode changes, auto-refresh Positions & Orders
    useEffect(() => {
        setPositionsRefreshKey((k) => k + 1);
    }, [tradeMode]);

    const handlePositionsRefresh = () => {
        setPositionsRefreshKey((k) => k + 1);
    };


    const goToOrders = () => {
        setActiveTab("orders");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goToPositions = () => {
        if (positionsRef.current) {
            const rect = positionsRef.current.getBoundingClientRect();
            const top = rect.top + window.scrollY - 80;
            window.scrollTo({ top, behavior: "smooth" });
        } else {
            setActiveTab("overview");
        }
    };

    return (
        <div className="relative p-4 sm:p-6 text-white space-y-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-400 mb-1">
                Paper &amp; Live Trading
            </h1>
            <p className="text-sm text-zinc-400 mb-2">
                Connect Alpaca, Tradier &amp; E*TRADE, let the AI find plays, then trade
                everything in one place.
            </p>

            <TradeModeBadge mode={tradeMode} />
            <div className="flex items-center gap-2 text-xs mt-2">
                <span className="text-zinc-400">Mode:
                    <InfoTooltip
                        text="Paper = simulated accounts with no real money. Live = your real broker accounts. Auto-trades follow whichever mode is selected here."
                    />
                </span>
                <button
                    onClick={() => setTradeMode("paper")}
                    className={
                        "px-3 py-1 rounded-full transition " +
                        (tradeMode === "paper"
                            ? "bg-emerald-500 text-black"
                            : "text-zinc-300 hover:bg-zinc-800")
                    }
                >
                    Paper
                </button>
                <button
                    onClick={() => setTradeMode("live")}
                    className={
                        "px-3 py-1 rounded-full transition " +
                        (tradeMode === "live"
                            ? "bg-pink-500 text-black"
                            : "text-zinc-300 hover:bg-zinc-800")
                    }
                >
                    Live
                </button>
            </div>
            <AutoStatusBanner userId={userId} token={token} tradeMode={tradeMode} />
            <div className="grid gap-3 md:grid-cols-2 mt-3">                
                <AutoTradeToggle userId={userId} token={token} />
                <AutoExitToggle userId={userId} token={token} />
            </div>
            {/* Tabs */}
            <div className="flex gap-2 border-b border-zinc-800 mb-4 overflow-x-auto">
                <TabButton
                    id="overview"
                    label="Overview"
                    icon="📊"
                    active={activeTab === "overview"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="orders"
                    label="Orders"
                    icon="💸"
                    active={activeTab === "orders"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="ai"
                    label="AI"
                    icon="🤖"
                    active={activeTab === "ai"}
                    onClick={setActiveTab}
                />
                <TabButton
                    id="settings"
                    label="Settings"
                    icon="⚙️"
                    active={activeTab === "settings"}
                    onClick={setActiveTab}
                />
            </div>

            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <BrokerHeader />
                        <div className="grid gap-4 md:grid-cols-3">
                            <BrokerConnectAlpaca userId={userId} mode={tradeMode} />
                            <BrokerConnectTradier userId={userId} mode={tradeMode} />
                            <BrokerConnectEtrade userId={userId} mode={tradeMode} token={token} />
                        </div>
                        <BalancesCard userId={userId} mode={tradeMode} />
                        <TradeAnalyticsCard userId={userId} token={token} mode={tradeMode} />
                        <AutoTradeRunsCard userId={userId} mode={tradeMode} />
                        <AllTradesCard userId={userId} mode={tradeMode} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock
                            userId={userId}
                            mode={tradeMode}
                            onRefresh={handlePositionsRefresh}
                            positionsRefreshKey={positionsRefreshKey}
                        />

                    </div>
                </div>
            )}

            {/* ORDERS TAB */}
            {activeTab === "orders" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <PlaceOrderCard userId={userId} />
                        <PlaceOptionOrderCard userId={userId} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock
                            userId={userId}
                            mode={tradeMode}
                            onRefresh={handlePositionsRefresh}
                            positionsRefreshKey={positionsRefreshKey}
                        />

                    </div>
                </div>
            )}

            {/* AI TAB */}
            {activeTab === "ai" && (
                <div className="space-y-4">
                    <div className="mb-2 flex items-center space-x-3 text-xs">
                        <label className="font-semibold text-white/80">Debug Mode</label>
                        <input
                            type="checkbox"
                            checked={debugMode}
                            onChange={() => setDebugMode((v) => !v)}
                            className="w-4 h-4"
                        />
                        <span className="text-xs text-white/50">
                            Show detailed exit reasoning when running Smart Exit
                        </span>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="space-y-4 lg:col-span-2">
                            <PicksButtons
                                userId={userId}
                                tradeMode={tradeMode}
                                token={token}
                            />
                            <AutoTradeReadiness userId={userId} token={token} tradeMode={tradeMode} />
                            <AutoTradeSummary userId={userId} token={token} />
                            <AutoTradeRunsCard userId={userId} mode={tradeMode} />
                            <SmartExitDebugCard
                                userId={userId}
                                tradeMode={tradeMode}
                                debugMode={debugMode}
                                token={token}
                            />
                        </div>
                        <div className="space-y-4">
                            <TradeSettingsCard userId={userId} token={token} mode={tradeMode} />

                        </div>
                    </div>
                </div>
            )}

            {/* SETTINGS TAB */}
            {activeTab === "settings" && (
                <div className="grid gap-4 lg:grid-cols-3">
                    <div className="space-y-4 lg:col-span-2">
                        <TradeSettingsCard userId={userId} token={token} mode={tradeMode} />
                        <AutoTradeRunsCard userId={userId} mode={tradeMode} />
                        <AllTradesCard userId={userId} mode={tradeMode} />
                    </div>
                    <div ref={positionsRef} className="space-y-4">
                        <UnifiedPositionsBlock
                            userId={userId}
                            mode={tradeMode}
                            onRefresh={handlePositionsRefresh}
                            positionsRefreshKey={positionsRefreshKey}
                        />

                    </div>
                </div>
            )}

            <FloatingTradeButtons
                onGoToOrders={goToOrders}
                onGoToPositions={goToPositions}
            />
        </div>
    );
}
